import React from "react";
import { useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import {
  IonContent, IonIcon, IonItem, IonLabel, IonList, IonMenu, IonMenuToggle, IonFooter,
} from '@ionic/react';
import {
  keyOutline, keySharp,
  lockClosedOutline, lockClosedSharp,
  gridOutline, gridSharp,
  cubeOutline, cubeSharp,
  expandOutline, expandSharp,
  bookmarkOutline, bookmarkSharp,
  shieldCheckmarkOutline, shieldCheckmarkSharp,
  personCircleOutline, personCircleSharp, manOutline, manSharp,
  banOutline, banSharp,
  peopleOutline, peopleSharp,
  cloudOutline, cloudSharp,
  timeOutline, timeSharp
} from 'ionicons/icons'

import './Menu.css'

import LanguageSelector   from "./LanguageSelector"

import {useLockingSystems} from "../../contexts/LockingSystemContext"
import {useAuth} from "../../contexts/AuthContext";

interface MenuItem {
  url:              string
  iosIcon:          string
  mdIcon:           string
  title:            string
  line?:            "none" | "full" | "inset" | undefined
  needsFeature?:    string
  needsUserOption?: string
}

const menuItems: MenuItem[] = [
  {
    title: 'dashboard.title',
    url: '/dashboard',
    iosIcon: gridOutline,
    mdIcon: gridSharp,
    line: 'full'
  },
  {
    title: 'locking-systems.title_list',
    url: '/locking-systems',
    iosIcon: bookmarkOutline,
    mdIcon: bookmarkSharp,
    line: 'full',
    needsUserOption: 'canViewLockingSystems'
  },
  {
    title: 'keys.keyGroup.keys_in_use.title_list',
    url: '/keys-in-use',
    iosIcon: keyOutline,
    mdIcon: keySharp,
    line: 'inset',
    needsUserOption: 'canViewKeysInUse'
  },
  {
    title: 'keys.keyGroup.keys_in_safe.title_list',
    url: '/keys-in-safe',
    iosIcon: cubeOutline,
    mdIcon: cubeSharp,
    line: 'inset',
    needsFeature: 'HAS_KEY_SAFE',
    needsUserOption: 'canViewKeysInSafe'
  },
  {
    title:           'keys.keyGroup.blocklist.title_list',
    url:             '/blocklist-keys',
    iosIcon:         banOutline,
    mdIcon:          banSharp,
    line:            'inset',
    needsFeature:    'HAS_KEY_BLOCKLIST',
    needsUserOption: 'canViewBlocklistKeys'
  },
  {
    title:           'locks.title_list',
    url:             '/locks',
    iosIcon:         lockClosedOutline,
    mdIcon:          lockClosedSharp,
    line:            'inset',
    needsUserOption: 'canViewLocks'
  },
  {
    title:           'people.title_list',
    url:             '/people',
    iosIcon:         manOutline,
    mdIcon:          manSharp,
    line:            'inset',
    needsUserOption: 'canViewPeople'
  },
  {
    title:           'person-groups.title_list',
    url:             '/person-groups',
    iosIcon:         peopleOutline,
    mdIcon:          peopleSharp,
    line:            'inset',
    needsFeature:    'HAS_PERSON_GROUPS',
    needsUserOption: 'canViewPersonGroups'
  },
  {
    title:   'locking-system-permissions.title_list',
    url:     '/locking-system-permissions',
    iosIcon: shieldCheckmarkOutline,
    mdIcon:  shieldCheckmarkSharp,
    line:    'inset',
    needsUserOption: 'canViewLockingSystemPermissions'
  },
  {
    title:   'locking-system-zones.title_list',
    url:     '/locking-system-zones',
    iosIcon: expandOutline,
    mdIcon:  expandSharp,
    line:    'inset',
    needsUserOption: 'canViewLockingSystemZones'
  },
  {
    title:           'time-limits.title_list',
    url:             '/time-limits',
    iosIcon:         timeOutline,
    mdIcon:          timeSharp,
    line:            'inset',
    needsUserOption: 'canViewTimeLimits',
    needsFeature:    'HAS_TIME_LIMITS',
  },
  {
    title:           'users.title_list',
    url:             '/users',
    iosIcon:         personCircleOutline,
    mdIcon:          personCircleSharp,
    line:            'full',
    needsUserOption: 'canViewUsers'
  },
  {
    title:           'data-sources.title_list',
    url:             '/data-sources',
    iosIcon:         cloudOutline,
    mdIcon:          cloudSharp,
    line:            'none',
    needsUserOption: 'canViewDataSources',
    needsFeature:    'HAS_DATA_SOURCES',
  },
];

const Menu: React.FC = () => {

  const location              = useLocation()
  const { t } = useTranslation()

  const { hasFeature: hasLockingSystemFeature } = useLockingSystems()
  const { hasOption: hasUserOption            } = useAuth()


  const menuRef  = React.useRef<HTMLIonMenuElement>(null)

  const locationPathParts: string[] = location.pathname.split('/')
  const locationPathMain: string    = '/' + locationPathParts[1] || ''

  return (
    <IonMenu ref={menuRef} contentId="main" type="overlay" style={{ 'maxWidth': '300px' }}>
      <IonContent forceOverscroll={false}>

        <IonList id="inbox-list">
          {menuItems.filter((menuItem) => {

            // return (menuItem.needsFeature && ! hasLockingSystemFeature(menuItem.needsFeature)) ? false : true

              if (menuItem.needsFeature && ! hasLockingSystemFeature(menuItem.needsFeature)) {
                return false
              }
              if (menuItem.needsUserOption && ! hasUserOption(menuItem.needsUserOption)) {
                return false
              }

              return true
          }).map((menuItem, index) => {

            // if (menuItem.needsFeature) {
            //   const hasFeature = hasLockingSystemFeature(menuItem.needsFeature)
            //
            //   if (! hasFeature) {
            //     return
            //   }
            // }

            return (
              <IonMenuToggle key={index} autoHide={false}>
                <IonItem className={locationPathMain === menuItem.url ? 'selected' : ''}
                  routerLink={menuItem.url}
                  lines={menuItem.line} detail={false}>
                  <IonIcon slot="start" ios={menuItem.iosIcon} md={menuItem.mdIcon} />
                  <IonLabel>{t(menuItem.title)}</IonLabel>
                </IonItem>
              </IonMenuToggle>
            );
          })}
        </IonList>

      </IonContent>
      <IonFooter className="ion-text-center ion-padding-vertical">
        <LanguageSelector color="bc-yellow" />

        <div className="ion-text-center" style={{ 'color': '#999999' }}>

          <p style={{ 'fontSize': '12px' }}>
            &copy; 2022-{new Date().getFullYear()}, {process.env.REACT_APP_BRAND_NAME}
          </p>
          <p style={{ 'fontSize': '11px' }}>
            v{process.env.REACT_APP_VERSION}
            {process.env.REACT_APP_ENV !== 'live' &&
                <span>&nbsp;- {process.env.REACT_APP_ENV}</span>
            }
          </p>

        </div>
      </IonFooter>
    </IonMenu>
  )
}

export default Menu